import { Connection } from '../../../types/electron';
import { useConnectionStore } from '../store/connectionStore';
import { getLanguageServiceProvider } from './language-services/languageServiceRegistry';
import type {
  LanguageServiceConnectionInfo,
  LanguageServiceProvider,
} from './language-services/languageServiceProvider';

let isInitialized = false;
let unsubscribeConnectionStore: (() => void) | undefined;

const documentUrisByConnectionId = new Map<string, Set<string>>();

/**
 * What a document's editor registered it with: the provider holding it and the
 * database it is bound to. Reconnects, retries and disconnects use THESE, not
 * the connection's engine and default database:
 * - a document lives in exactly one provider, and connecting it through
 *   another one (the connection's engine, when the editor's engine differed
 *   for a moment) left a copy there that nothing ever closed;
 * - a tab or notebook bound to a database other than the connection's default
 *   lost it on reconnect, and IntelliSense then served the default database.
 * An editor switching database registers a new document URI, so the binding is
 * always the editor's current database.
 */
export type DocumentBinding = {
  readonly provider: LanguageServiceProvider;
  readonly database: string;
};

const bindingByDocumentUri = new Map<string, DocumentBinding>();

// Track pending IntelliSense connections for retry when SQL Tools Service becomes ready
type PendingConnection = {
  connection: Connection;
  documentUri: string;
  retryCount: number;
};
const pendingIntelliSenseConnections = new Map<string, PendingConnection>();
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY_MS = 2000;

function getProviderForConnection(connection: Connection) {
  const legacyType = (connection as unknown as { type?: unknown }).type;
  const engine =
    connection.engine ||
    (typeof legacyType === 'string' ? legacyType : undefined) ||
    'mssql';

  return getLanguageServiceProvider(engine);
}

function getProviderForDocument(connection: Connection, documentUri: string): LanguageServiceProvider {
  return bindingByDocumentUri.get(documentUri)?.provider ?? getProviderForConnection(connection);
}

function getOrCreateDocumentUris(connectionId: string): Set<string> {
  const existing = documentUrisByConnectionId.get(connectionId);
  if (existing) {
    return existing;
  }

  const next = new Set<string>();
  documentUrisByConnectionId.set(connectionId, next);
  return next;
}

function getDocumentUrisForConnection(connectionId: string): string[] {
  return Array.from(documentUrisByConnectionId.get(connectionId) ?? []);
}

function getServerName(connection: Connection): string {
  const serverParts = connection.server.split('\\');
  return serverParts[0] || connection.server;
}

function getPendingKey(connectionId: string, documentUri: string): string {
  return `${connectionId}:${documentUri}`;
}

/**
 * Latest sync attempt per pending key. A connect that settles after a NEWER
 * attempt started for the same document (reconnect while the old load was
 * still in flight) is superseded: its outcome must neither clear the newer
 * attempt's pending retry nor consume its retry budget.
 */
const latestAttemptByKey = new Map<string, number>();

async function syncConnectionToDocument(
  connection: Connection,
  documentUri: string,
  isRetry: boolean = false
): Promise<void> {
  const serverName = getServerName(connection);
  const provider = getProviderForDocument(connection, documentUri);
  const pendingKey = getPendingKey(connection.id, documentUri);
  const attempt = (latestAttemptByKey.get(pendingKey) ?? 0) + 1;
  latestAttemptByKey.set(pendingKey, attempt);
  const superseded = (): boolean => latestAttemptByKey.get(pendingKey) !== attempt;

  // Fire and forget initialization - don't block on it
  provider.ensureInitialized?.().catch((error) => {
    console.warn(
      '[ConnectionSyncManager] Language service provider initialization failed (will retry):',
      error
    );
  });

  const connectionInfo: LanguageServiceConnectionInfo = {
    connectionId: connection.id,
    database: bindingByDocumentUri.get(documentUri)?.database || connection.database || 'master',
    host: serverName,
    user: connection.login || '',
  };

  try {
    const success = await provider.connectForLanguageServices(
      documentUri,
      connectionInfo
    );

    // A newer attempt owns this document now — this outcome is nobody's.
    if (superseded()) return;

    if (success) {
      // Success - remove from pending if it was there
      pendingIntelliSenseConnections.delete(pendingKey);
      console.log(
        `[ConnectionSyncManager] IntelliSense connected for ${connection.id} (${isRetry ? 'retry' : 'initial'})`
      );
    } else {
      // Failed but didn't throw - schedule retry
      scheduleRetry(connection, documentUri, pendingKey);
    }
  } catch (error) {
    if (superseded()) return;
    // Error during connection - schedule retry
    console.warn(
      `[ConnectionSyncManager] IntelliSense connection failed for ${connection.id}, will retry in background:`,
      error instanceof Error ? error.message : error
    );
    scheduleRetry(connection, documentUri, pendingKey);
  }
}

/**
 * A retry can only help while the connection still exists in the store AND
 * the document is still registered for it. A load that came back `false`
 * because the document was disconnected on purpose (the connection closed —
 * entry removed — while `.show schema` / the schema warm was in flight) or
 * because its tab was closed (unregistered) must not be retried: the retry
 * would re-point a disconnected document at a closed connection — or
 * recreate a closed one in the provider's registry and feed its schema into
 * the worker. A connection merely LOST (entry kept with status
 * `disconnected`) stays retry-able: the bounded chain is what picks the
 * document up after an in-place reconnect, which changes no entry count and
 * so never reaches `handleNewConnection`. Engine-neutral — every provider's
 * connect returns `false` in those cases.
 */
function canRetry(connectionId: string, documentUri: string): boolean {
  const exists = useConnectionStore
    .getState()
    .activeConnections.some((conn) => conn.id === connectionId);
  return exists && (documentUrisByConnectionId.get(connectionId)?.has(documentUri) ?? false);
}

function scheduleRetry(connection: Connection, documentUri: string, pendingKey: string): void {
  if (!canRetry(connection.id, documentUri)) {
    pendingIntelliSenseConnections.delete(pendingKey);
    return;
  }
  const existing = pendingIntelliSenseConnections.get(pendingKey);
  const retryCount = existing ? existing.retryCount + 1 : 1;

  if (retryCount > MAX_RETRY_COUNT) {
    console.warn(
      `[ConnectionSyncManager] Max retries (${MAX_RETRY_COUNT}) reached for IntelliSense connection ${connection.id}. IntelliSense may not be available.`
    );
    pendingIntelliSenseConnections.delete(pendingKey);
    return;
  }

  pendingIntelliSenseConnections.set(pendingKey, {
    connection,
    documentUri,
    retryCount,
  });

  // Schedule a retry after delay
  setTimeout(() => {
    const pending = pendingIntelliSenseConnections.get(pendingKey);
    if (pending && !canRetry(pending.connection.id, pending.documentUri)) {
      // Connection closed or tab closed while waiting for the retry.
      pendingIntelliSenseConnections.delete(pendingKey);
      return;
    }
    if (pending) {
      console.log(
        `[ConnectionSyncManager] Retrying IntelliSense connection (attempt ${pending.retryCount}/${MAX_RETRY_COUNT})`
      );
      void syncConnectionToDocument(pending.connection, pending.documentUri, true);
    }
  }, RETRY_DELAY_MS * retryCount);
}

async function disconnectConnectionFromDocument(
  connection: Connection,
  documentUri: string
): Promise<void> {
  const provider = getProviderForDocument(connection, documentUri);
  const disconnect = provider.disconnectForLanguageServices ?? provider.closeDocument;

  try {
    const success = await disconnect(documentUri);

    if (success) {
    } else {
      console.error(
        `[ConnectionSyncManager] Failed to disconnect language services for connection ${connection.id}`
      );
    }
  } catch (error) {
    console.error(`[ConnectionSyncManager] Error disconnecting:`, error);
  }
}

function handleNewConnection(connection: Connection): void {
  const documentUris = getDocumentUrisForConnection(connection.id);
  for (const documentUri of documentUris) {
    void syncConnectionToDocument(connection, documentUri);
  }
}

function handleConnectionClosed(connection: Connection): void {
  const documentUris = getDocumentUrisForConnection(connection.id);
  for (const documentUri of documentUris) {
    // Remove from pending if it was waiting for retry
    const pendingKey = getPendingKey(connection.id, documentUri);
    pendingIntelliSenseConnections.delete(pendingKey);

    void disconnectConnectionFromDocument(connection, documentUri);
  }
}

function cleanupAllConnections(): void {
  for (const [connectionId, documentUris] of documentUrisByConnectionId.entries()) {
    const activeConnection = useConnectionStore
      .getState()
      .activeConnections.find((conn) => conn.id === connectionId);

    const connectionProvider = activeConnection
      ? getProviderForConnection(activeConnection.connection)
      : getLanguageServiceProvider('mssql');

    for (const documentUri of documentUris) {
      const provider = bindingByDocumentUri.get(documentUri)?.provider ?? connectionProvider;
      const disconnect = provider.disconnectForLanguageServices ?? provider.closeDocument;
      void disconnect(documentUri).catch((error) => {
        console.error(
          `[ConnectionSyncManager] Error disconnecting connection ${connectionId}:`,
          error
        );
      });
    }
  }

  documentUrisByConnectionId.clear();
  bindingByDocumentUri.clear();
}

export function initializeConnectionSyncManager(): void {
  if (isInitialized) {
    return;
  }

  isInitialized = true;

  // (The former SSTS-ready retry accelerator — a `window.sqlLanguageServer
  // .onStatusChange` listener that re-kicked pending IntelliSense connections
  // when SSTS reported ready — was removed in Plan 20 §10 step 7's fix round:
  // mssql language services no longer involve SSTS, so the event never fires
  // on the document path; the bounded per-connection retries below are, and
  // always were, the actual recovery mechanism.)

  unsubscribeConnectionStore = useConnectionStore.subscribe((state, prevState) => {
    if (state.activeConnections.length > prevState.activeConnections.length) {
      const newConnections = state.activeConnections.filter(
        (newConn) =>
          !prevState.activeConnections.some((oldConn) => oldConn.id === newConn.id)
      );

      for (const newConn of newConnections) {
        handleNewConnection(newConn.connection);
      }
    }

    if (state.activeConnections.length < prevState.activeConnections.length) {
      const closedConnections = prevState.activeConnections.filter(
        (oldConn) =>
          !state.activeConnections.some((newConn) => newConn.id === oldConn.id)
      );

      for (const closedConn of closedConnections) {
        handleConnectionClosed(closedConn.connection);
      }
    }
  });

  const initialState = useConnectionStore.getState();
  if (initialState.activeConnections.length > 0) {
    initialState.activeConnections.forEach((conn) => {
      handleNewConnection(conn.connection);
    });
  }

  window.addEventListener('beforeunload', () => {
    unsubscribeConnectionStore?.();
    pendingIntelliSenseConnections.clear();
    cleanupAllConnections();
  });
}

export function registerDocumentForConnection(
  connectionId: string,
  documentUri: string,
  /** The provider and database the editor registered the document with. */
  binding?: DocumentBinding,
): void {
  getOrCreateDocumentUris(connectionId).add(documentUri);
  if (binding) bindingByDocumentUri.set(documentUri, binding);
}

export function unregisterDocumentForConnection(
  connectionId: string,
  documentUri: string
): void {
  bindingByDocumentUri.delete(documentUri);
  const documentUris = documentUrisByConnectionId.get(connectionId);
  if (!documentUris) {
    return;
  }

  // A closed tab has nothing to retry — and a retry would recreate it. Its
  // attempt counter goes too (a settle for a deleted key reads `undefined
  // !== attempt` → still superseded).
  const pendingKey = getPendingKey(connectionId, documentUri);
  pendingIntelliSenseConnections.delete(pendingKey);
  latestAttemptByKey.delete(pendingKey);
  documentUris.delete(documentUri);
  if (documentUris.size === 0) {
    documentUrisByConnectionId.delete(connectionId);
  }
}
